import React, { useEffect, useState } from 'react';
import { camposConfigApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Plus, Pencil, Trash2, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';

const ENTIDADES = [
  { v: 'empresa',  label: 'Empresas' },
  { v: 'contacto', label: 'Contactos' },
  { v: 'contrato', label: 'Contratos' },
];
const TIPOS_DATO = [
  { v: 'texto', l: 'Texto' }, { v: 'numero', l: 'Número' }, { v: 'fecha', l: 'Fecha' },
  { v: 'booleano', l: 'Sí/No' }, { v: 'seleccion_simple', l: 'Selección simple' }, { v: 'seleccion_multiple', l: 'Selección múltiple' },
];
const ROLES = [
  { v: 'admin', l: 'Admin' },
  { v: 'dir_comercial', l: 'Dir. Comercial' },
  { v: 'comercial', l: 'Comercial' },
  { v: 'agente_libre', l: 'Agente libre' },
];

export default function CamposPersonalizados() {
  const [tab, setTab] = useState('empresa');
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState({ open: false, data: null });

  const blank = () => ({
    entidad_tipo: tab, clave: '', label: '', tipo_dato: 'texto', opciones: [],
    orden: 999,
    visible_para: ['admin', 'dir_comercial', 'comercial', 'agente_libre'],
    editable_por: ['admin', 'dir_comercial', 'comercial'],
    activo: true,
  });

  const load = async () => {
    const data = await camposConfigApi.list({ entidad_tipo: tab, solo_activos: false });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const save = async () => {
    try {
      const d = { ...dialog.data };
      // seleccion debe tener opciones
      if (['seleccion_simple', 'seleccion_multiple'].includes(d.tipo_dato)) {
        d.opciones = (d.opciones || []).filter((o) => o.trim());
        if (!d.opciones.length) { toast.error('Añade al menos una opción'); return; }
      } else {
        d.opciones = [];
      }
      if (!d.clave) d.clave = d.label;  // el backend hace slug
      if (d.id) {
        const id = d.id; delete d.id; delete d.entidad_tipo; delete d.clave;
        await camposConfigApi.update(id, d);
        toast.success('Campo actualizado');
      } else {
        await camposConfigApi.create(d);
        toast.success('Campo creado');
      }
      setDialog({ open: false, data: null });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Desactivar este campo? Los datos guardados no se borran.')) return;
    try { await camposConfigApi.delete(id); toast.success('Desactivado'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="campos-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight">Campos personalizados</h1>
          <p className="text-sm text-ink-500 mt-1">Amplía las fichas de empresas, contactos y contratos sin tocar código</p>
        </div>
        <Button onClick={() => setDialog({ open: true, data: blank() })} data-testid="btn-nuevo-campo">
          <Plus className="w-4 h-4 mr-2" /> Nuevo campo
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-ink-200">
        {ENTIDADES.map((e) => (
          <button
            key={e.v}
            onClick={() => setTab(e.v)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === e.v ? 'border-brand-500 text-brand-700' : 'border-transparent text-ink-500 hover:text-ink-800'}`}
            data-testid={`tab-${e.v}`}
          >
            {e.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-ink-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-ink-600 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-3">Etiqueta</th>
              <th className="text-left px-4 py-3">Clave</th>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">Visible para</th>
              <th className="text-center px-4 py-3">Estado</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-ink-400">No hay campos personalizados para {tab}</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="hover:bg-ink-50">
                <td className="px-4 py-3 font-medium">{c.label}</td>
                <td className="px-4 py-3 font-mono text-xs text-ink-500">{c.clave}</td>
                <td className="px-4 py-3">{TIPOS_DATO.find((t) => t.v === c.tipo_dato)?.l || c.tipo_dato}</td>
                <td className="px-4 py-3 text-xs text-ink-500">{(c.visible_para || []).join(', ')}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${c.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-100 text-ink-500'}`}>
                    {c.activo ? 'activo' : 'inactivo'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setDialog({ open: true, data: { ...c } })}><Pencil className="w-3.5 h-3.5" /></Button>
                  {c.activo && <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => remove(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={dialog.open} onOpenChange={(v) => setDialog({ open: v, data: v ? dialog.data : null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" />{dialog.data?.id ? 'Editar campo' : 'Nuevo campo'}</DialogTitle>
          </DialogHeader>
          {dialog.data && (
            <div className="space-y-3">
              <div>
                <Label>Etiqueta (texto visible) *</Label>
                <Input value={dialog.data.label} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, label: e.target.value } }))} data-testid="input-label" />
              </div>
              <div>
                <Label>Tipo de dato</Label>
                <Select value={dialog.data.tipo_dato} onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, tipo_dato: v } }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS_DATO.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {['seleccion_simple', 'seleccion_multiple'].includes(dialog.data.tipo_dato) && (
                <div>
                  <Label>Opciones (una por línea)</Label>
                  <textarea
                    className="w-full rounded-md border border-ink-200 text-sm p-2 min-h-24"
                    value={(dialog.data.opciones || []).join('\n')}
                    onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, opciones: e.target.value.split('\n') } }))}
                  />
                </div>
              )}
              <div>
                <Label>Visible para</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {ROLES.map((r) => {
                    const on = (dialog.data.visible_para || []).includes(r.v);
                    return (
                      <button
                        key={r.v} type="button"
                        onClick={() => setDialog((d) => {
                          const cur = d.data.visible_para || [];
                          return { ...d, data: { ...d.data, visible_para: cur.includes(r.v) ? cur.filter((x) => x !== r.v) : [...cur, r.v] } };
                        })}
                        className={`px-2.5 py-1 rounded text-xs ${on ? 'bg-brand-500 text-white' : 'bg-ink-100 text-ink-700'}`}
                      >{r.l}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Editable por</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {ROLES.map((r) => {
                    const on = (dialog.data.editable_por || []).includes(r.v);
                    return (
                      <button
                        key={r.v} type="button"
                        onClick={() => setDialog((d) => {
                          const cur = d.data.editable_por || [];
                          return { ...d, data: { ...d.data, editable_por: cur.includes(r.v) ? cur.filter((x) => x !== r.v) : [...cur, r.v] } };
                        })}
                        className={`px-2.5 py-1 rounded text-xs ${on ? 'bg-emerald-500 text-white' : 'bg-ink-100 text-ink-700'}`}
                      >{r.l}</button>
                    );
                  })}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!dialog.data.activo} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, activo: e.target.checked } }))} />
                Activo
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, data: null })}>Cancelar</Button>
            <Button onClick={save} data-testid="btn-guardar-campo">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
